/*
 * AAIKO store locator behaviour. One custom element, guarded so repeated
 * sections on a page never redefine it.
 *
 *   aaiko-store-locator   country tabs + free-text search over the store list
 *
 * Nothing here is drawn. The Figma frames carry one unfiltered state with the
 * "All" tab filled, so the filled/outline pair is read as selected/unselected
 * and everything below is a decision, not a spec:
 *
 *   - a country tab and the search box combine: searching inside a country
 *     stays inside it, the way the FAQ tabs already behave.
 *   - a country group whose stores are all filtered out is hidden with its
 *     heading, rather than left standing above nothing.
 *   - with every store filtered out, the no-results line appears. There is no
 *     drawn frame for it; it borrows the address type.
 *   - AAIKO, 03/09/2026: "de lijst is te lang, graag inkorten dat je niet zo
 *     lang moet scrollen". Each country group shows the section's
 *     `stores_per_country` rows and reveals its own toggle for the rest.
 *     A search term or a country tab always shows every match, cap or not,
 *     so shortening the list can never hide a store the visitor asked for.
 *
 * Without JavaScript every store renders and no toggle appears, which is the
 * drawn state.
 */
(function () {
  'use strict';

  function normalise(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  if (customElements.get('aaiko-store-locator')) return;

  customElements.define(
    'aaiko-store-locator',
    class AaikoStoreLocator extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-aaiko-store-tab]'));
        this.groups = Array.prototype.slice.call(this.querySelectorAll('[data-aaiko-store-group]'));
        this.stores = Array.prototype.slice.call(this.querySelectorAll('[data-aaiko-store]'));
        this.search = this.querySelector('[data-aaiko-store-search]');
        this.empty = this.querySelector('[data-aaiko-store-empty]');
        this.country = '';
        this.cap = parseInt(this.getAttribute('data-aaiko-locator-cap') || '0', 10) || 0;

        this.groups.forEach(
          function (group) {
            var more = group.querySelector('[data-aaiko-store-more]');
            if (!more) return;
            more.addEventListener(
              'click',
              function () {
                var open = group.hasAttribute('data-aaiko-store-expanded');
                if (open) group.removeAttribute('data-aaiko-store-expanded');
                else group.setAttribute('data-aaiko-store-expanded', '');
                this.apply();
                /* Collapsing from the bottom of a long group would leave the
                   viewport below the list; the toggle keeps itself in view. */
                if (open) more.scrollIntoView({ block: 'center', behavior: 'instant' });
              }.bind(this)
            );
          }.bind(this)
        );

        this.tabs.forEach(
          function (tab, index) {
            tab.addEventListener(
              'click',
              function () {
                this.select(index, false);
              }.bind(this)
            );
            tab.addEventListener('keydown', this.onTabKeydown.bind(this, index));
          }.bind(this)
        );

        if (this.search) {
          this.search.addEventListener('input', this.apply.bind(this));
        }

        this.apply();
      }

      /* Arrow keys move along the row; the tabs are toggle buttons, so every
         one of them stays in the tab order. */
      onTabKeydown(index, event) {
        var last = this.tabs.length - 1;
        var next = null;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;

        if (next === null) return;
        event.preventDefault();
        this.tabs[next].focus();
      }

      select(index, focus) {
        var chosen = this.tabs[index];
        if (!chosen) return;

        this.country = chosen.getAttribute('data-aaiko-store-tab') || '';

        this.tabs.forEach(
          function (tab) {
            var on = tab === chosen;
            tab.setAttribute('aria-pressed', on ? 'true' : 'false');
            tab.classList.toggle('aaiko-locator__tab--on', on);
          }.bind(this)
        );

        if (focus) chosen.focus();
        this.apply();
      }

      apply() {
        var term = normalise(this.search ? this.search.value : '').trim();
        var country = normalise(this.country);
        var cap = this.cap;
        var visible = 0;

        this.groups.forEach(function (group) {
          var expanded = group.hasAttribute('data-aaiko-store-expanded');
          var stores = Array.prototype.slice.call(group.querySelectorAll('[data-aaiko-store]'));
          var matched = 0;
          var shown = 0;

          stores.forEach(function (store) {
            var haystack = normalise(store.getAttribute('data-aaiko-store-text'));
            var inCountry = !country || normalise(store.getAttribute('data-aaiko-store-country')) === country;
            var matches = !term || haystack.indexOf(term) !== -1;
            var qualifies = inCountry && matches;

            if (qualifies) matched += 1;

            /* The cap applies to browsing only: a search shows every hit. */
            var capped = cap > 0 && !expanded && !term && qualifies && shown >= cap;
            var show = qualifies && !capped;

            store.hidden = !show;
            if (show) {
              shown += 1;
              visible += 1;
            }
          });

          /* A group with nothing left in it goes, heading and all. */
          group.hidden = matched === 0;

          var more = group.querySelector('[data-aaiko-store-more]');
          if (!more) return;
          var useable = cap > 0 && !term && matched > cap;
          more.hidden = !useable;
          more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          more.textContent = expanded
            ? more.getAttribute('data-aaiko-store-more-less') || more.textContent
            : more.getAttribute('data-aaiko-store-more-all') || more.textContent;
        });

        if (this.empty) this.empty.hidden = visible !== 0;
      }
    }
  );
})();
